/**
 * Dual dimmer device template
 * Used by: SPDM-002PE01EU
 */

import {
	createTurnAction,
	createStateCondition,
	createStateChangedTrigger,
} from '../../common/stateManagement.js';
import { createPowerLevelCondition, createPowerThresholdTrigger } from '../../common/powerMonitoring.js';
import {
	createSetBrightnessAction,
	createBrightnessLevelCondition,
	createBrightnessChangedTrigger,
} from '../../common/brightnessManagement.js';

export function createDualDimmerTemplate() {
	return {
		deviceType: 'dual_dimmer',
		channels: ['light:0', 'light:1'],
		actions: {
			turn: createTurnAction('Light.Set', 'output'),
			setBrightness: createSetBrightnessAction(),
			// Dual-specific action
			turnAllChannels: {
				label: 'Turn All Channels',
				description: 'Control both dimmer channels simultaneously',
				channels: ['light:0', 'light:1'],
				parameters: [
					{
						name: 'on',
						label: 'State',
						type: 'boolean',
						description: 'Turn both channels on (true) or off (false)',
					},
					{
						name: 'brightness',
						label: 'Brightness',
						type: 'number',
						min: 1,
						max: 100,
						unit: '%',
						description: 'Set brightness level for both channels (1-100%)',
						optional: true,
						showWhen: { parameter: 'on', value: true },
					},
					{
						name: 'transition',
						label: 'Transition Duration',
						type: 'number',
						min: 0.1,
						max: 30.0,
						unit: 's',
						description: 'Fade transition duration in seconds',
						optional: true,
					},
				],
				generateCommand: (deviceId, channel, params) => {
					// Return array of commands for both channels
					return [
						{
							id: 1,
							method: 'Light.Set',
							params: {
								id: 0,
								on: params.on,
								...(params.brightness && { brightness: params.brightness }),
								...(params.transition && { transition_duration: params.transition }),
							},
						},
						{
							id: 2,
							method: 'Light.Set',
							params: {
								id: 1,
								on: params.on,
								...(params.brightness && { brightness: params.brightness }),
								...(params.transition && { transition_duration: params.transition }),
							},
						},
					];
				},
			},
		},
		conditions: {
			isOn: createStateCondition('output'),
			brightnessLevel: createBrightnessLevelCondition(),
			allChannelsOn: {
				label: 'All Channels On',
				description: 'Check if both channels are on',
				channels: ['light:0', 'light:1'],
				parameters: [],
				evaluateCondition: (deviceState) => {
					const light0State = deviceState.status?.['light:0'];
					const light1State = deviceState.status?.['light:1'];
					return light0State?.output === true && light1State?.output === true;
				},
			},
			totalPowerConsumption: {
				label: 'Total Power Consumption',
				description: 'Check combined power consumption of both channels',
				channels: ['light:0', 'light:1'],
				parameters: [
					{
						name: 'operator',
						label: 'Comparison',
						type: 'select',
						options: [
							{ value: 'gt', label: 'Greater than' },
							{ value: 'gte', label: 'Greater or equal' },
							{ value: 'lt', label: 'Less than' },
							{ value: 'lte', label: 'Less or equal' },
						],
						description: 'Comparison operator',
					},
					{
						name: 'power',
						label: 'Power Value',
						type: 'number',
						min: 0,
						max: 5000,
						unit: 'W',
						description: 'Target total power consumption in Watts',
					},
				],
				evaluateCondition: (deviceState, channel, params) => {
					const light0State = deviceState.status?.['light:0'];
					const light1State = deviceState.status?.['light:1'];
					const totalPower = (light0State?.apower || 0) + (light1State?.apower || 0);

					switch (params.operator) {
						case 'gt':
							return totalPower > params.power;
						case 'gte':
							return totalPower >= params.power;
						case 'lt':
							return totalPower < params.power;
						case 'lte':
							return totalPower <= params.power;
						default:
							return false;
					}
				},
			},
		},
		triggers: {
			stateChanged: createStateChangedTrigger('output'),
			brightnessChanged: createBrightnessChangedTrigger(),
			allChannelsStateChanged: {
				label: 'All Channels State Changed',
				description: 'Triggered when both channels change state simultaneously',
				channels: ['light:0', 'light:1'],
				parameters: [
					{
						name: 'to',
						label: 'Changed To',
						type: 'select',
						options: [
							{ value: 'both_on', label: 'Both turned on' },
							{ value: 'both_off', label: 'Both turned off' },
							{ value: 'any', label: 'Any simultaneous change' },
						],
						description: 'State change pattern',
					},
				],
				detectTrigger: (oldState, newState, channel, params) => {
					const old0Output = oldState?.status?.['light:0']?.output;
					const old1Output = oldState?.status?.['light:1']?.output;
					const new0Output = newState?.status?.['light:0']?.output;
					const new1Output = newState?.status?.['light:1']?.output;

					const ch0Changed = old0Output !== new0Output;
					const ch1Changed = old1Output !== new1Output;

					// Both channels must change simultaneously
					if (!ch0Changed || !ch1Changed) return false;

					switch (params.to) {
						case 'both_on':
							return new0Output === true && new1Output === true;
						case 'both_off':
							return new0Output === false && new1Output === false;
						case 'any':
							return true;
						default:
							return false;
					}
				},
			},
		},
	};
}
